import {Navigate, useSearchParams} from 'react-router-dom';
import {Construction} from 'lucide-react';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {PageHeader} from '@common-web/components/page-header';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {OverviewTab} from '@internal/pages/claims/OverviewTab';
import {ExCodesTab} from '@internal/pages/claims/ExCodesTab';
import {GroupRollupTab} from '@internal/pages/claims/GroupRollupTab';
import {ClaimsTab} from '@internal/pages/claims/ClaimsTab';

const TABS = ['overview', 'ex-codes', 'groups-ex', 'claims'] as const;
type TabKey = (typeof TABS)[number];

export function ClaimsTrackingDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewClaims = useHasPermission(CODE_PERMISSIONS.claimsTracking.view.key);

  if (!canViewClaims) {
    return <Navigate to='/groups' replace />;
  }

  const tabParam = searchParams.get('tab');
  const tab: TabKey = TABS.includes(tabParam as TabKey) ? (tabParam as TabKey) : 'overview';

  const goToTab = (value: TabKey, extra?: Record<string, string | null>) => {
    setSearchParams((prev) => {
      prev.set('tab', value);
      if (extra) {
        for (const [key, val] of Object.entries(extra)) {
          if (val === null) prev.delete(key);
          else prev.set(key, val);
        }
      }
      return prev;
    });
  };

  const drillToGroup = (groupId: string) => goToTab('claims', {group: groupId});
  const drillToExCode = (exCode: string) => goToTab('claims', {exCode, stage: null});

  return (
    <div className='flex flex-col gap-6 p-6 max-w-[1600px] mx-auto w-full'>
      <PageHeader
        title='Claims tracking'
        description='Where every VBA claim sits in the adjudication pipeline — Claim and ClaimBatch unified.'
      />

      <Alert className='border-amber-200 bg-amber-50 text-amber-950 [&>svg]:text-amber-700'>
        <Construction className='h-4 w-4' />
        <AlertTitle>In development</AlertTitle>
        <AlertDescription>
          This feature is under active development. Numbers and workflows may change — use for
          review and feedback only.
        </AlertDescription>
      </Alert>

      <Tabs value={tab} onValueChange={(value) => goToTab(value as TabKey)}>
        <TabsList>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='ex-codes'>Ex codes</TabsTrigger>
          <TabsTrigger value='groups-ex'>Groups · ex codes</TabsTrigger>
          <TabsTrigger value='claims'>Claims</TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='mt-4'>
          <OverviewTab
            onDrillToExCodes={() => goToTab('ex-codes')}
            onDrillToGroup={drillToGroup}
            onDrillToExCode={drillToExCode}
          />
        </TabsContent>

        <TabsContent value='ex-codes' className='mt-4'>
          <ExCodesTab />
        </TabsContent>

        <TabsContent value='groups-ex' className='mt-4'>
          <GroupRollupTab onSelectGroup={drillToGroup} />
        </TabsContent>

        <TabsContent value='claims' className='mt-4'>
          <ClaimsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
